import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import LandingPage from "@/components/LandingPage";
import AuthPage from "@/components/AuthPage";
import Dashboard from "@/components/Dashboard";
import WorkoutLibrary from "@/components/WorkoutLibrary";
import WorkoutPlayer from "@/components/WorkoutPlayer";
import WorkoutHistory from "@/components/WorkoutHistory";
import CustomWorkoutBuilder from "@/components/CustomWorkoutBuilder";
import AIPlanGenerator from "@/components/AIPlanGenerator";
import NutritionPage from "@/components/NutritionPage";
import ProfilePage from "@/components/ProfilePage";
import AnalyticsPage from "@/components/AnalyticsPage";
import LeaderboardsPage from "@/components/LeaderboardsPage";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsOfService from "@/components/TermsOfService";
import ContactPage from "@/components/ContactPage";
import ChangelogPage from "@/components/ChangelogPage";
import AdminPage from "@/components/AdminPage";
import ReferralsPage from "@/components/ReferralsPage";
import AchievementsPage from "@/components/AchievementsPage";
import Onboarding from "@/components/Onboarding";
import StressReset from "@/components/StressReset";
import Challenges from "@/components/Challenges";
import BuddyPage from "@/components/BuddyPage";
import PricingPage from "@/components/PricingPage";
import PaymentSuccess from "@/components/PaymentSuccess";
import AppLayout from "@/components/AppLayout";
import NotFound from "@/components/NotFound";

import InstallPrompt from "@/components/InstallPrompt";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.onboarded) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.onboarded) return <Navigate to="/dashboard" replace />;
  if (user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/workouts" element={<ProtectedRoute><AppLayout><WorkoutLibrary /></AppLayout></ProtectedRoute>} />
      <Route path="/workouts/player" element={<ProtectedRoute><AppLayout><WorkoutPlayer /></AppLayout></ProtectedRoute>} />
      <Route path="/workouts/history" element={<ProtectedRoute><AppLayout><WorkoutHistory /></AppLayout></ProtectedRoute>} />
      <Route path="/workouts/custom" element={<ProtectedRoute><AppLayout><CustomWorkoutBuilder /></AppLayout></ProtectedRoute>} />
      <Route path="/ai-plan" element={<ProtectedRoute><AppLayout><AIPlanGenerator /></AppLayout></ProtectedRoute>} />
      <Route path="/nutrition" element={<ProtectedRoute><AppLayout><NutritionPage /></AppLayout></ProtectedRoute>} />
      <Route path="/stress-reset" element={<ProtectedRoute><AppLayout><StressReset /></AppLayout></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><AppLayout><Challenges /></AppLayout></ProtectedRoute>} />
      <Route path="/buddy" element={<ProtectedRoute><AppLayout><BuddyPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><AppLayout><PricingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><AppLayout><PaymentSuccess /></AppLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AppLayout><AnalyticsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/leaderboards" element={<ProtectedRoute><AppLayout><LeaderboardsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><AppLayout><ReferralsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><AppLayout><AchievementsPage /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = 'GRIT.FIT | Home Workout - No Equipment';
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
